import {
  IconBuildingBank,
  IconCheck,
  IconClipboardCheck,
  IconClipboardList,
  IconFileCheck,
  IconFileInvoice,
  IconFileText,
  IconNotes,
  IconReceipt,
  IconStack,
  IconUser,
  type IconProps,
} from "@tabler/icons-react";
import type { ComponentType } from "react";

/**
 * Catálogo declarativo de los 8 pasos del flujo FB60 que renderiza el
 * Stepper. Mantén sincronizado con `state-mapping.ts` · cada estado del
 * backend mapea a uno de estos índices.
 *
 * El step 1 (CFDI) y 4 (Prorrateo) tienen variantes por subtipo
 * (CFDI único vs Múltiples CFDI) · esa lógica vive en
 * `SolicitudStepper` (rewriting label/description según subtipo).
 */
export interface StepDef {
  key: PasoSolicitud;
  label: string;
  description: string;
  Icon: ComponentType<IconProps>;
}

export type PasoSolicitud =
  | "crear"
  | "contrato"
  | "orden"
  | "organizacion"
  | "cfdi"
  | "datos"
  | "proveedor"
  | "prorrateo"
  | "documentos"
  | "autorizacion"
  | "conciliacion"
  | "contabilizacion";

export const STEPS_BASE: ReadonlyArray<StepDef> = [
  {
    key: "crear",
    label: "Tipo de solicitud",
    description: "Tipo y subtipo",
    Icon: IconClipboardList,
  },
  {
    key: "cfdi",
    label: "CFDI",
    description: "Carga y validación",
    Icon: IconFileInvoice,
  },
  {
    key: "datos",
    label: "Datos generales",
    description: "Motivo y periodo",
    Icon: IconNotes,
  },
  {
    key: "proveedor",
    label: "Proveedor",
    description: "Asignación",
    Icon: IconUser,
  },
  {
    key: "prorrateo",
    label: "Prorrateo",
    description: "Líneas contables",
    Icon: IconStack,
  },
  {
    key: "documentos",
    label: "Documentos",
    description: "Soporte",
    Icon: IconClipboardCheck,
  },
  {
    key: "contabilizacion",
    label: "Contabilización",
    description: "Pólizas SAP",
    Icon: IconReceipt,
  },
  {
    key: "autorizacion",
    label: "Autorización",
    description: "Flujo de niveles",
    Icon: IconCheck,
  },
];

/**
 * Paso "Contrato anualizado" · exclusivo del tipo Contrato Anualizado (CA).
 * Se inserta entre "Tipo de solicitud" y "CFDI".
 */
const CONTRATO_STEP: StepDef = {
  key: "contrato",
  label: "Contrato anualizado",
  description: "Contrato anualizado",
  Icon: IconFileText,
};

/**
 * Paso "Orden de compra" · exclusivo del tipo Orden de Compra (OC).
 * Se inserta entre "Tipo de solicitud" y "CFDI".
 */
const ORDEN_STEP: StepDef = {
  key: "orden",
  label: "Orden de compra",
  description: "Orden de compra",
  Icon: IconFileText,
};

/**
 * P44 · FB60 Contrapago · el CFDI no existe al iniciar (llega DESPUÉS del
 * pago) · reemplaza el paso "CFDI" por la selección manual de organización.
 */
const ORGANIZACION_STEP: StepDef = {
  key: "organizacion",
  label: "Organización",
  description: "Selección manual",
  Icon: IconBuildingBank,
};

/**
 * P44 · FB60 Contrapago · paso final donde se adjunta el CFDI real y se
 * corre la conciliación (RFC/moneda/total vs lo capturado manualmente).
 */
const CONCILIACION_STEP: StepDef = {
  key: "conciliacion",
  label: "Conciliar CFDI",
  description: "CFDI real (post-pago)",
  Icon: IconFileCheck,
};

/**
 * Devuelve la lista ordenada de pasos según el tipo/subtipo de solicitud.
 * FB60 usa los 8 pasos base; Contrato Anualizado inserta "Contrato
 * anualizado" y Orden de Compra inserta "Orden de compra", ambos tras el
 * paso inicial de tipo. Contrapago (subtipo de FB60) reemplaza "CFDI" por
 * "Organización" y agrega "Conciliar CFDI" al final (P44).
 */
export function stepsParaTipo(
  tipo: string | null | undefined,
  subtipo?: string | null,
): StepDef[] {
  if (subtipo === "CONTRAPAGO") {
    const [crear, , datos, proveedor, prorrateo, documentos, contabilizacion, autorizacion] =
      STEPS_BASE;
    return [
      crear,
      ORGANIZACION_STEP,
      datos,
      proveedor,
      prorrateo,
      documentos,
      contabilizacion,
      autorizacion,
      CONCILIACION_STEP,
    ];
  }
  // CA/OC Invoice: el proveedor se deriva del contrato/orden · no hay paso de proveedor
  // (a diferencia de los CFDI, el Invoice no trae un emisor que elegir manualmente).
  const esInvoiceConDocumento =
    subtipo === "INVOICE" && (tipo === "ContratoAnualizado" || tipo === "OrdenDeCompra");
  const sinProveedor = (steps: StepDef[]) =>
    esInvoiceConDocumento ? steps.filter((s) => s.key !== "proveedor") : steps;

  if (tipo === "ContratoAnualizado") {
    return sinProveedor([STEPS_BASE[0], CONTRATO_STEP, ...STEPS_BASE.slice(1)]);
  }
  if (tipo === "OrdenDeCompra") {
    return sinProveedor([STEPS_BASE[0], ORDEN_STEP, ...STEPS_BASE.slice(1)]);
  }
  return [...STEPS_BASE];
}

/**
 * Estados de baja terminal · cuando la solicitud cae acá, el stepper
 * se oculta y se muestra un alert "el flujo no continúa".
 */
export const TERMINAL_NEGATIVOS = new Set<string>([
  "DadaDeBajaPorAutorizador",
  "CanceladaPorSolicitante",
  "DescartadaPorPlazoVencido",
]);
